import { forwardRef, useState } from 'react';
import { IconSizes, EyeIcon, EyeOffIcon } from '@newcore/icons';
import { Button } from '@newcore/ui/buttons';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { Sizes } from '@newcore/ui/enums/sizes';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { ButtonVariants } from '@newcore/ui/enums/variants';
import { Text } from '@newcore/ui/typography';
import { LoginInput, type LoginInputProps } from '@/components/ui/login-input';
import { StatusMessage } from '@/components/ui/status-message';

// className targets the field shell; inputClassName targets the inner <input>.
export interface InputFieldProps extends LoginInputProps {
  label?: string;
  error?: string;
  isRequired?: boolean;
  inputType?: 'text' | 'email' | 'password';
}

export const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  (props, ref) => {
    const {
      label,
      error,
      isRequired = false,
      inputType = 'text',
      ...inputProps
    } = props;

    const hasError = Boolean(error);
    const [showPassword, setShowPassword] = useState(false);
    const isPassword = inputType === 'password';

    return (
      <div className='flex flex-col gap-2 w-full'>
        {label && (
          <div className='flex gap-1 items-center'>
            <Text
              as='label'
              size={TextSizes.SM}
              weight={FontWeights.MEDIUM}
              className='opacity-80 text-primary'
            >
              {label}
            </Text>
            {isRequired && (
              <Text
                as='span'
                size={TextSizes.XS}
                className='text-error-primary'
              >
                *
              </Text>
            )}
          </div>
        )}

        <LoginInput
          {...inputProps}
          ref={ref}
          type={isPassword && showPassword ? 'text' : inputType}
          aria-invalid={hasError || undefined}
          trailing={
            isPassword ? (
              <Button
                type='button'
                variant={ButtonVariants.ICON}
                size={Sizes.ICON}
                className='size-6'
                onClick={() => setShowPassword(prev => !prev)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOffIcon
                    size={IconSizes.SM}
                    className='text-fg-quaternary'
                  />
                ) : (
                  <EyeIcon size={IconSizes.SM} className='text-fg-quaternary' />
                )}
              </Button>
            ) : undefined
          }
        />

        {hasError && <StatusMessage centered={false}>{error}</StatusMessage>}
      </div>
    );
  }
);

InputField.displayName = 'InputField';
